import { calculateBestQuotes, type QuoteMarketWithBestBidOffer } from '@src/utils/rfq';
import {
  BlotterTable,
  Button,
  ButtonVariants,
  FormControlSizes,
  IconName,
  isPricingModeRelative,
  isTerminalQuoteStatus,
  isValidQuotePrice,
  SideEnum,
  Tooltip,
  useBlotterObservables,
  useBlotterTable,
  type ColumnDef,
  type ExpiryCountdownParams,
  type PriceParams,
  type PricingModeEnum,
  type Quote,
  type QuoteStatusColumnParams,
  type QuoteStatusEnum,
  type SizeColumnParams,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';

import type { QuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';

interface SweeperMarketsBlotterProps {
  quoteSweepingData: QuoteSweepingQuoteMarkets;
  side: SideEnum;
  selectedMarketNames: Set<string>;
  onToggleMarket: (marketName: string) => void;
}

interface SweeperMarketRow {
  rowID: string;
  quote: Quote | undefined;
  quoteMarket: QuoteMarketWithBestBidOffer;
  side: SideEnum;
  Symbol: string | undefined;
  Currency: string | undefined;
  PricingMode: PricingModeEnum | undefined;
  QuoteStatus: QuoteStatusEnum | undefined;
  Text: string | undefined;
  expiryTime: string | undefined;
  bid: QuoteMarketWithBestBidOffer;
  offer: QuoteMarketWithBestBidOffer;
  bidQty: string | undefined;
  offerQty: string | undefined;
  bidDisplayPx: string | undefined;
  offerDisplayPx: string | undefined;
  isInSweeper: boolean;
}

interface SweeperMarketActionColumnValue {
  marketName: string;
  isInSweeper: boolean;
  notTradableTooltip: string | undefined;
}

export function SweeperMarketsBlotter({
  quoteSweepingData,
  side,
  selectedMarketNames,
  onToggleMarket,
}: SweeperMarketsBlotterProps) {
  const rows = useMemo(() => {
    const { representativeQuote, sweeperQuotes } = quoteSweepingData;
    const quoteByMarket = new Map(sweeperQuotes.map(({ quote, quoteMarket }) => [quoteMarket.Market, quote]));
    const quoteMarkets = sweeperQuotes.map(({ quoteMarket }) => quoteMarket);
    const quoteMarketsWithBestBidOffer = calculateBestQuotes({
      quotes: quoteMarkets,
      pricingMode: representativeQuote.PricingMode,
    });

    return quoteMarketsWithBestBidOffer.map(quoteMarket =>
      newSweeperMarketRow(
        quoteByMarket.get(quoteMarket.Market),
        quoteMarket,
        side,
        selectedMarketNames.has(quoteMarket.Market)
      )
    );
  }, [quoteSweepingData, selectedMarketNames, side]);
  const columns = useSweeperMarketsColumns(side, onToggleMarket);
  const { dataObservable, errorObservable } = useBlotterObservables({ data: rows });

  const blotterTable = useBlotterTable<SweeperMarketRow>({
    dataObservable,
    errorObservable,
    columns,
    rowID: 'rowID',
  });

  return <BlotterTable {...blotterTable} background="colors.gray.020" />;
}

function useSweeperMarketsColumns(
  side: SideEnum,
  onToggleMarket: SweeperMarketsBlotterProps['onToggleMarket']
): ColumnDef<SweeperMarketRow>[] {
  return useMemo<ColumnDef<SweeperMarketRow>[]>(() => {
    const shouldShowBidColumns = side === SideEnum.Sell;

    return [
      getSweeperMarketActionColumn(onToggleMarket),
      {
        field: 'expiryTime',
        type: 'expiryCountdown',
        title: 'Expiry',
        initialWidth: 130,
        params: {
          isExpired: (data: unknown) =>
            isSweeperMarketRow(data) && !!data.QuoteStatus && isTerminalQuoteStatus(data.QuoteStatus),
        } satisfies ExpiryCountdownParams<SweeperMarketRow>,
      },
      {
        type: 'market',
        field: 'bid.Market',
        title: 'Bid Maker',
        initialWidth: 150,
        hide: !shouldShowBidColumns,
      },
      {
        type: 'size',
        field: 'bidQty',
        id: 'BidQty',
        title: 'Bid Qty',
        initialWidth: 90,
        hide: !shouldShowBidColumns,
        params: { currencyField: 'Currency' } satisfies SizeColumnParams<SweeperMarketRow>,
      },
      {
        type: 'price',
        field: 'bidDisplayPx',
        id: 'BidPx',
        title: 'Bid Px',
        initialWidth: 80,
        hide: !shouldShowBidColumns,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<SweeperMarketRow>,
      },
      {
        type: 'market',
        field: 'offer.Market',
        title: 'Offer Maker',
        initialWidth: 150,
        hide: shouldShowBidColumns,
      },
      {
        type: 'size',
        field: 'offerQty',
        id: 'OfferQty',
        title: 'Offer Qty',
        initialWidth: 90,
        hide: shouldShowBidColumns,
        params: {
          currencyField: 'Currency',
        } satisfies SizeColumnParams<SweeperMarketRow>,
      },
      {
        type: 'price',
        field: 'offerDisplayPx',
        title: 'Offer Px',
        id: 'OfferPx',
        initialWidth: 80,
        hide: shouldShowBidColumns,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<SweeperMarketRow>,
      },
      {
        field: 'QuoteStatus',
        type: 'quoteStatus',
        title: 'Status',
        params: {
          tooltipField: 'Text',
        } satisfies QuoteStatusColumnParams<SweeperMarketRow>,
      },
    ] satisfies ColumnDef<SweeperMarketRow>[];
  }, [onToggleMarket, side]);
}

function getSweeperMarketActionColumn(
  onToggleMarket: SweeperMarketsBlotterProps['onToggleMarket']
): ColumnDef<SweeperMarketRow> {
  return {
    type: 'custom',
    id: 'sweepAction',
    title: '',
    width: 75,
    pinned: 'right',
    sortable: false,
    params: {
      valueGetter: (params: ValueGetterParams<SweeperMarketRow>): SweeperMarketActionColumnValue | undefined => {
        if (!params.data) {
          return undefined;
        }

        return {
          marketName: params.data.quoteMarket.Market,
          isInSweeper: params.data.isInSweeper,
          notTradableTooltip: getNotTradableTooltip(params.data),
        };
      },
      cellRenderer: ({ value }: ICellRendererParams<SweeperMarketRow, SweeperMarketActionColumnValue>) => {
        if (!value) {
          return <></>;
        }

        const button = (
          <Button
            size={FormControlSizes.Tiny}
            startIcon={IconName.Scale}
            disabled={!!value.notTradableTooltip}
            onClick={() => onToggleMarket(value.marketName)}
            data-testid={`sweeper-market-${value.marketName}-toggle`}
            variant={value.isInSweeper ? ButtonVariants.Priority : ButtonVariants.Default}
          >
            Sweep
          </Button>
        );

        return (
          <Tooltip tooltip={value.notTradableTooltip} usePortal={true}>
            {button}
          </Tooltip>
        );
      },
    } satisfies ColDef<SweeperMarketRow>,
  };
}

function getNotTradableTooltip(row: SweeperMarketRow): string | undefined {
  const price = row.side === SideEnum.Buy ? row.quoteMarket.OfferPx : row.quoteMarket.BidPx;

  if (isValidQuotePrice(price, row.QuoteStatus, row.PricingMode)) {
    return undefined;
  }

  return 'This quote is not tradable.';
}

function isSweeperMarketRow(data: unknown): data is SweeperMarketRow {
  return typeof data === 'object' && data != null && 'QuoteStatus' in data;
}

function newSweeperMarketRow(
  quote: Quote | undefined,
  quoteMarket: QuoteMarketWithBestBidOffer,
  side: SideEnum,
  isInSweeper: boolean
): SweeperMarketRow {
  return {
    rowID: quoteMarket.Market,
    quote,
    quoteMarket,
    side,
    Symbol: quote?.Symbol,
    Currency: quote?.Currency,
    PricingMode: quote?.PricingMode,
    QuoteStatus: quoteMarket.QuoteStatus,
    Text: quoteMarket.Text,
    expiryTime: quoteMarket.ValidUntilTime,
    bid: quoteMarket,
    offer: quoteMarket,
    bidQty: quoteMarket.BidQty || quote?.OrderQty,
    offerQty: quoteMarket.OfferQty || quote?.OrderQty,
    bidDisplayPx: isValidQuotePrice(quoteMarket.BidPx, quoteMarket.QuoteStatus, quote?.PricingMode)
      ? quoteMarket.BidPx
      : undefined,
    offerDisplayPx: isValidQuotePrice(quoteMarket.OfferPx, quoteMarket.QuoteStatus, quote?.PricingMode)
      ? quoteMarket.OfferPx
      : undefined,
    isInSweeper,
  };
}
